import type { Class } from '../../types/tournament';

import { MatchScheduleInterval } from './scheduleConflictDetector';

export function resolveParentClassId(
  classId: number,
  classIdToObject: Record<string, Class>
): number | null {
  const classObject =
    classIdToObject[String(classId)] ?? classIdToObject[classId];
  const parentClassId = classObject?.parentClassId;
  if (parentClassId == null) return null;
  return Number(parentClassId);
}

export function childClassIds(
  parentClassId: number,
  classIdToObject: Record<string, Class>
): number[] {
  const ids: number[] = [];
  for (const key of Object.keys(classIdToObject)) {
    const classObject = classIdToObject[key];
    if (classObject?.parentClassId == null) continue;
    if (Number(classObject.parentClassId) !== parentClassId) continue;
    const classId = Number(classObject.id ?? key);
    if (!ids.includes(classId)) ids.push(classId);
  }
  return ids;
}

export function getMaxParentClassEndMs(
  childClassId: number,
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>
): { maxEndMs: number; latestMatchId: number } | null {
  const parentClassId = resolveParentClassId(childClassId, classIdToObject);
  if (parentClassId == null) return null;

  const parentIntervals = intervals.filter(
    (i) => i.match.classId === parentClassId && i.match.scheduleDate
  );
  if (parentIntervals.length === 0) return null;

  let maxEndMs = -Infinity;
  let latestMatchId = parentIntervals[0].match.id;
  for (const interval of parentIntervals) {
    if (interval.endMs > maxEndMs) {
      maxEndMs = interval.endMs;
      latestMatchId = interval.match.id;
    }
  }
  return { maxEndMs, latestMatchId };
}

export function getMinChildClassStartMs(
  parentClassId: number,
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>
): { minStartMs: number; earliestMatchId: number } | null {
  const childIds = new Set(childClassIds(parentClassId, classIdToObject));
  if (childIds.size === 0) return null;

  let minStartMs = Infinity;
  let earliestMatchId = 0;
  let found = false;
  for (const interval of intervals) {
    if (!childIds.has(interval.match.classId)) continue;
    if (!interval.match.scheduleDate) continue;
    if (interval.startMs < minStartMs) {
      minStartMs = interval.startMs;
      earliestMatchId = interval.match.id;
      found = true;
    }
  }
  if (!found) return null;
  return { minStartMs, earliestMatchId };
}

export function hasChildBeforeParentConflict(
  childInterval: MatchScheduleInterval,
  allIntervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>
): boolean {
  const parentInfo = getMaxParentClassEndMs(
    childInterval.match.classId,
    allIntervals,
    classIdToObject
  );
  if (!parentInfo) return false;
  return childInterval.startMs < parentInfo.maxEndMs;
}

export function hasParentClassOrderingConflict(
  childInterval: MatchScheduleInterval,
  allIntervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>
): boolean {
  return hasChildBeforeParentConflict(childInterval, allIntervals, classIdToObject);
}

export function hasParentAfterChildConflict(
  parentInterval: MatchScheduleInterval,
  allIntervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>
): boolean {
  if (resolveParentClassId(parentInterval.match.classId, classIdToObject) != null) {
    return false;
  }
  const childInfo = getMinChildClassStartMs(
    parentInterval.match.classId,
    allIntervals,
    classIdToObject
  );
  if (!childInfo) return false;
  const childStartMin = childInfo.minStartMs;
  return (
    parentInterval.endMs > childStartMin || parentInterval.startMs >= childStartMin
  );
}
